import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faEye,
  faFileCode,
  faFileWord,
} from "@fortawesome/free-solid-svg-icons";

import localLawService from "api/localLawService";
import {
  ELegalActRegisterKind,
  ILegalActListItem,
  ILegalActRegisterNode,
} from "dto/LocalLaw/ILocalLaw";
import PreviewModal from "modules/localLawModule/PreviewModal";
import useOpenLegalAct from "modules/localLawModule/useOpenLegalAct";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

// Rejestry → Prawo miejscowe — port magistrackiego LegalActRegister (Documents.Register.UI):
// przełącznik rodzaju (Uchwały / Zarządzenia / Pozostałe), po lewej drzewo chronologiczne
// (kadencja → rok → sesja dla uchwał, kadencja → rok → miesiąc dla reszty — drzewo buduje BE,
// ten sam builder co w portalu radnego), po prawej lista aktów zawężona klikniętym węzłem
// i tekstem z wyszukiwarki panelu. Rejestr obejmuje akty uchwalone/wydane (bez projektów).

const KINDS: {
  kind: ELegalActRegisterKind;
  label: string;
  dateLabel: string;
}[] = [
  {
    kind: ELegalActRegisterKind.Uchwaly,
    label: "Uchwały",
    dateLabel: "data uchwalenia",
  },
  {
    kind: ELegalActRegisterKind.Zarzadzenia,
    label: "Zarządzenia",
    dateLabel: "data zarządzenia",
  },
  {
    kind: ELegalActRegisterKind.Pozostale,
    label: "Pozostałe",
    dateLabel: "data aktu",
  },
];

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

/** Nagłówek wiersza: „Uchwała Nr XIV/122/2026" albo sam rodzaj, gdy nie ma numeru. */
const actHeading = (act: ILegalActListItem) =>
  act.ActNumber ? `${act.ActTypeName} Nr ${act.ActNumber}` : act.ActTypeName;

/** Domyślnie rozwinięta pierwsza (najnowsza) kadencja i jej najnowszy rok — jak w portalu. */
const initialExpanded = (nodes: ILegalActRegisterNode[]) => {
  const open = new Set<string>();
  const first = nodes[0];
  if (first) {
    open.add(first.Key);
    const firstYear = first.Children[0];
    if (firstYear) open.add(firstYear.Key);
  }
  return open;
};

const findNode = (
  nodes: ILegalActRegisterNode[],
  key: string
): ILegalActRegisterNode | null => {
  for (const node of nodes) {
    if (node.Key === key) return node;
    const child = findNode(node.Children, key);
    if (child) return child;
  }
  return null;
};

interface Props {
  searchText: string;
  setSubtitle: (s: string) => void;
}

const LocalLawRegister = ({ searchText, setSubtitle }: Props) => {
  const [kind, setKind] = useState<ELegalActRegisterKind>(
    ELegalActRegisterKind.Uchwaly
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [previewFor, setPreviewFor] = useState<ILegalActListItem | null>(null);

  const { open: openInWord, busy: opening } = useOpenLegalAct();

  const { data: treeData, isLoading: loadingTree } =
    localLawService.useGetRegisterTree(kind);
  const tree: ILegalActRegisterNode[] = useMemo(
    () => treeData?.data ?? [],
    [treeData]
  );

  // Nowe drzewo → domyślne rozwinięcie. Wybór węzła czyścimy już przy zmianie gałęzi
  // (changeKind), bo klucze kadencji/roku/miesiąca powtarzają się między gałęziami i stary
  // klucz dopasowałby się do zbuforowanego drzewa nowej gałęzi na jeden render.
  useEffect(() => {
    setExpanded(initialExpanded(tree));
  }, [tree]);

  const changeKind = (next: ELegalActRegisterKind) => {
    setSelectedKey(null);
    setKind(next);
  };

  const selectedNode = useMemo(
    () => (selectedKey ? findNode(tree, selectedKey) : null),
    [tree, selectedKey]
  );

  const text = searchText.trim();
  const { data: actsData, isLoading: loadingActs } =
    localLawService.useGetRegisterActs({
      Kind: kind,
      TermId: selectedNode?.Filter.TermId ?? null,
      Year: selectedNode?.Filter.Year ?? null,
      Month: selectedNode?.Filter.Month ?? null,
      Date: selectedNode?.Filter.Date ?? null,
      Session: selectedNode?.Filter.Session ?? null,
      Text: text || null,
    });
  const acts: ILegalActListItem[] = useMemo(
    () => actsData?.data?.Items ?? [],
    [actsData]
  );
  // BE tnie listę do limitu — pokazujemy „X z Y", żeby licznik nie kłócił się z drzewem.
  const actsTotal = actsData?.data?.Total ?? acts.length;

  const kindDef = KINDS.find((k) => k.kind === kind)!;

  useEffect(() => {
    const scope = selectedNode
      ? `${selectedNode.Name}${
          selectedNode.Desc ? ` (${selectedNode.Desc})` : ""
        }`
      : "wszystkie";
    setSubtitle(`${kindDef.label} · ${scope}`);
  }, [kindDef.label, selectedNode, setSubtitle]);

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const total = useMemo(() => tree.reduce((s, t) => s + t.Count, 0), [tree]);

  const renderNode = (node: ILegalActRegisterNode, level: number) => {
    const isOpen = expanded.has(node.Key);
    const canExpand = node.Children.length > 0;
    const labelClass =
      node.Type === 0
        ? styles.treeLabelTerm
        : node.Type === 1
        ? styles.treeLabelYear
        : "";

    return (
      <div key={node.Key}>
        <div
          className={`${styles.treeItem} ${
            selectedKey === node.Key ? styles.treeItemActive : ""
          }`}
          style={{ paddingLeft: 4 + level * 16 }}
        >
          <button
            type="button"
            className={styles.treeExpander}
            onClick={() => canExpand && toggle(node.Key)}
            aria-label={isOpen ? "Zwiń" : "Rozwiń"}
            tabIndex={canExpand ? 0 : -1}
          >
            {canExpand && (
              <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
            )}
          </button>
          <button
            type="button"
            className={`${styles.treeLabel} ${labelClass}`}
            onClick={() => {
              setSelectedKey(node.Key);
              if (canExpand && !isOpen) toggle(node.Key);
            }}
          >
            <span className={styles.treeName}>{node.Name}</span>
            {node.Desc && <span className={styles.treeDesc}>{node.Desc}</span>}
            <span className={styles.treeCount}>{node.Count}</span>
          </button>
        </div>
        {isOpen && node.Children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.kinds}>
          {KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              className={`${styles.kindBtn} ${
                kind === k.kind ? styles.kindBtnActive : ""
              }`}
              onClick={() => changeKind(k.kind)}
            >
              {k.label}
            </button>
          ))}
        </div>
        <div className={styles.toolbarInfo}>
          {loadingActs
            ? "Ładowanie…"
            : actsTotal > acts.length
            ? `Aktów: pokazano ${acts.length} z ${actsTotal} — zawęź drzewem lub wyszukiwarką`
            : `Aktów: ${acts.length}`}
          {!loadingActs && !selectedNode && !text
            ? ` (w rejestrze: ${total})`
            : ""}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.tree}>
          <div
            className={`${styles.treeItem} ${styles.treeAll} ${
              !selectedKey ? styles.treeItemActive : ""
            }`}
          >
            <span className={styles.treeExpander} />
            <button
              type="button"
              className={`${styles.treeLabel} ${styles.treeLabelTerm}`}
              onClick={() => setSelectedKey(null)}
            >
              <span className={styles.treeName}>
                Wszystkie {kindDef.label.toLowerCase()}
              </span>
              <span className={styles.treeCount}>{total}</span>
            </button>
          </div>
          {loadingTree ? (
            <div className={styles.empty}>Ładowanie…</div>
          ) : tree.length === 0 ? (
            <div className={styles.empty}>Brak aktów w rejestrze.</div>
          ) : (
            tree.map((node) => renderNode(node, 0))
          )}
        </div>

        <div className={styles.list}>
          {loadingActs ? (
            <div className={styles.empty}>Ładowanie…</div>
          ) : acts.length === 0 ? (
            <div className={styles.empty}>
              {text
                ? "Brak aktów pasujących do wyszukiwania."
                : "Brak aktów w wybranym zakresie."}
            </div>
          ) : (
            acts.map((act) => (
              <div key={act.Id} className={styles.actRow}>
                <div className={styles.actMain}>
                  <span className={styles.actTitle}>{actHeading(act)}</span>
                  <span className={styles.actMeta}>{act.Title || "—"}</span>
                  <span className={styles.actMeta}>
                    {kindDef.dateLabel}:{" "}
                    {formatDate(act.AdoptedDate ?? act.Date)}
                    {act.PublicationDate
                      ? ` · ogłoszono: ${formatDate(act.PublicationDate)}`
                      : ""}
                    {act.EffectiveFrom
                      ? ` · wchodzi w życie: ${formatDate(act.EffectiveFrom)}`
                      : ""}
                    {act.Author ? ` · ${act.Author}` : ""}
                  </span>
                  <div className={styles.badges}>
                    <span className={`${styles.badge} ${styles.badgeStatus}`}>
                      {act.StatusName}
                    </span>
                    {kind === ELegalActRegisterKind.Pozostale && (
                      <span className={styles.badge}>{act.ActTypeName}</span>
                    )}
                    {act.PublishInBip && (
                      <span className={`${styles.badge} ${styles.badgeBip}`}>
                        BIP
                      </span>
                    )}
                    {(act.Repeal || act.PartialRepeal) && (
                      <span className={`${styles.badge} ${styles.badgeWarn}`}>
                        {act.Repeal ? "nieważny" : "nieważny w części"}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.rowTools}>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Podgląd aktu (treść wraz z załącznikami)"
                    onClick={() => setPreviewFor(act)}
                  >
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                  <button
                    type="button"
                    className={styles.toolBtn}
                    title="Otwórz w edytorze Word"
                    disabled={opening}
                    onClick={() => openInWord(act.DocumentId)}
                  >
                    <FontAwesomeIcon icon={faFileWord} />
                  </button>
                  {act.HasXml && (
                    <button
                      type="button"
                      className={styles.toolBtn}
                      title="Pobierz XML aktu"
                      onClick={() =>
                        localLawService
                          .downloadFile(act.Id, "xml", `${actHeading(act)}.xml`)
                          .catch(() =>
                            notification({
                              type: ENotificationType.ERROR,
                              title: "Błąd",
                              text: "Nie udało się pobrać XML.",
                            })
                          )
                      }
                    >
                      <FontAwesomeIcon icon={faFileCode} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {previewFor && (
        <PreviewModal act={previewFor} onClose={() => setPreviewFor(null)} />
      )}
    </div>
  );
};

export default LocalLawRegister;
